// JobAdvertising.js
import React, { useState, useEffect } from 'react';
import './JobAdvertisingStyles.css';

const JobAdvertising = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    department: '',
    location: '',
    jobType: ''
  });
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    departments: 0
  });

  const jobListings = [
    {
      id: 1,
      title: 'Senior Frontend Developer',
      company: 'Tech Innovations Inc.',
      location: 'San Francisco, CA',
      type: 'Full-time',
      department: 'Engineering',
      salary: '$120,000 - $150,000',
      postedDate: '2024-01-15',
      experience: '5+ years',
      description: 'We are seeking a highly skilled Senior Frontend Developer to lead our client-facing application development. You will work with cutting-edge technologies and collaborate with cross-functional teams to deliver exceptional user experiences.',
      responsibilities: [
        'Develop and maintain responsive web applications using React.js',
        'Collaborate with UX/UI designers to implement pixel-perfect designs',
        'Optimize applications for maximum speed and scalability',
        'Mentor junior developers and conduct code reviews',
        'Implement automated testing and continuous integration'
      ],
      requirements: [
        '5+ years of professional React.js development experience',
        'Expert knowledge of TypeScript and modern JavaScript (ES6+)',
        'Experience with state management libraries (Redux, Zustand)',
        'Proficiency in CSS-in-JS solutions and responsive design',
        'Familiarity with GraphQL and RESTful APIs'
      ],
      benefits: [
        'Competitive salary and equity package',
        'Comprehensive health, dental, and vision insurance',
        'Flexible remote work options',
        'Professional development budget',
        'Unlimited PTO and paid parental leave'
      ],
      status: 'active',
      featured: true,
      urgent: false
    },
    {
      id: 2,
      title: 'HR Business Partner',
      company: 'Global Solutions Ltd.',
      location: 'New York, NY',
      type: 'Full-time',
      department: 'Human Resources',
      salary: '$90,000 - $110,000',
      postedDate: '2024-01-10',
      experience: '7+ years',
      description: 'As an HR Business Partner, you will serve as a strategic partner to business leaders, driving people initiatives and fostering a positive work environment that promotes growth and excellence.',
      responsibilities: [
        'Partner with senior leadership to develop HR strategies',
        'Manage employee relations and conflict resolution',
        'Oversee performance management and career development',
        'Drive diversity, equity, and inclusion initiatives',
        'Analyze HR metrics to inform business decisions'
      ],
      requirements: [
        '7+ years of progressive HR experience',
        'SHRM or HRCI certification preferred',
        'Strong knowledge of employment laws and regulations',
        'Excellent communication and interpersonal skills',
        'Experience in organizational development'
      ],
      benefits: [
        'Performance-based bonuses',
        'Comprehensive benefits package',
        'Hybrid work model',
        'Leadership development programs',
        'Wellness and fitness stipend'
      ],
      status: 'active',
      featured: true,
      urgent: true
    },
    {
      id: 3,
      title: 'Product Manager',
      company: 'Innovate Tech',
      location: 'Remote',
      type: 'Full-time',
      department: 'Product',
      salary: '$130,000 - $160,000',
      postedDate: '2024-01-08',
      experience: '6+ years',
      description: 'Lead product development from conception to launch, working closely with engineering, design, and marketing teams to deliver innovative solutions that meet market needs.',
      responsibilities: [
        'Define product vision and roadmap',
        'Conduct market research and competitive analysis',
        'Gather and prioritize product requirements',
        'Collaborate with engineering on technical feasibility',
        'Analyze product performance and user feedback'
      ],
      requirements: [
        '6+ years of product management experience',
        'Proven track record of successful product launches',
        'Strong analytical and problem-solving skills',
        'Experience with Agile/Scrum methodologies',
        'Excellent stakeholder management abilities'
      ],
      benefits: [
        'Equity participation',
        'Remote-first culture',
        'Learning and development budget',
        'Quarterly team offsites',
        'Comprehensive health coverage'
      ],
      status: 'active',
      featured: false,
      urgent: false
    },
    {
      id: 4,
      title: 'Data Scientist',
      company: 'Analytics Pro',
      location: 'Boston, MA',
      type: 'Full-time',
      department: 'Data Science',
      salary: '$110,000 - $140,000',
      postedDate: '2024-01-05',
      experience: '4+ years',
      description: 'Join our data science team to extract insights from complex datasets and build predictive models that drive business decisions and product innovation.',
      responsibilities: [
        'Develop machine learning models and algorithms',
        'Analyze large-scale structured and unstructured data',
        'Collaborate with product teams on data-driven features',
        'Create data visualizations and dashboards',
        'Communicate findings to technical and non-technical stakeholders'
      ],
      requirements: [
        '4+ years of experience in data science',
        'Proficiency in Python, R, and SQL',
        'Experience with machine learning frameworks',
        'Strong statistical analysis background',
        'Knowledge of big data technologies'
      ],
      benefits: [
        'Research publication support',
        'Conference attendance opportunities',
        'Flexible work arrangements',
        'Competitive bonus structure',
        'State-of-the-art equipment'
      ],
      status: 'active',
      featured: false,
      urgent: true
    }
  ];

  // Calculate stats
  useEffect(() => {
    setStats({
      totalJobs: jobListings.length,
      activeJobs: jobListings.filter(job => job.status === 'active').length,
      departments: [...new Set(jobListings.map(job => job.department))].length
    });
  }, []);

  const filteredJobs = jobListings.filter(job => 
    job.status === activeTab && 
    (job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
     job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
     job.department.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (filters.department === '' || job.department === filters.department) &&
    (filters.location === '' || job.location.includes(filters.location)) &&
    (filters.jobType === '' || job.type === filters.jobType)
  );

  const departments = [...new Set(jobListings.map(job => job.department))];
  const locations = [...new Set(jobListings.map(job => job.location))];
  const jobTypes = [...new Set(jobListings.map(job => job.type))];

  return (
    <div className="jobAdvertisingContainer">
      {/* Hero Section */}
      <section className="jobAdvertisingHero">
        <div className="jobAdvertisingHeroContent">
          <div className="jobAdvertisingHeroText">
            <h1 className="jobAdvertisingMainTitle">
              Build Your Career With 
              <span className="jobAdvertisingGradientText"> Exceptional Opportunities</span>
            </h1>
            <p className="jobAdvertisingHeroDescription">
              Join innovative companies and work on cutting-edge projects. 
              We connect top talent with forward-thinking organizations. 
              Discover your next career move in our curated job listings.
            </p>
            <div className="jobAdvertisingHeroStats">
              <div className="jobAdvertisingStatItem">
                <span className="jobAdvertisingStatNumber">{stats.totalJobs}+</span>
                <span className="jobAdvertisingStatLabel">Active Positions</span>
              </div>
              <div className="jobAdvertisingStatItem">
                <span className="jobAdvertisingStatNumber">{stats.departments}</span>
                <span className="jobAdvertisingStatLabel">Departments</span>
              </div>
              <div className="jobAdvertisingStatItem">
                <span className="jobAdvertisingStatNumber">50+</span>
                <span className="jobAdvertisingStatLabel">Partner Companies</span>
              </div>
            </div>
          </div>
          <div className="jobAdvertisingHeroVisual">
            <div className="jobAdvertisingAnimatedIllustration">
              <div className="jobAdvertisingFloatingElement jobAdvertisingElement1">🚀</div>
              <div className="jobAdvertisingFloatingElement jobAdvertisingElement2">💼</div>
              <div className="jobAdvertisingFloatingElement jobAdvertisingElement3">🌟</div>
              <div className="jobAdvertisingFloatingElement jobAdvertisingElement4">🎯</div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="jobAdvertisingSearchSection">
        <div className="jobAdvertisingSearchContainer">
          <h2 className="jobAdvertisingSectionTitle">Find Your Perfect Role</h2>
          <p className="jobAdvertisingSectionSubtitle">
            Browse through our carefully curated job opportunities and find the position that matches your skills and aspirations.
          </p>
          
          <div className="jobAdvertisingSearchBox">
            <div className="jobAdvertisingSearchInputWrapper">
              <span className="jobAdvertisingSearchIcon">🔍</span>
              <input
                type="text"
                placeholder="Search by job title, company, keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="jobAdvertisingSearchInput"
              />
            </div>
            <button className="jobAdvertisingSearchButton">
              Search Jobs
            </button>
          </div>

          <div className="jobAdvertisingFilterGrid">
            <div className="jobAdvertisingFilterGroup">
              <label className="jobAdvertisingFilterLabel">Department</label>
              <select 
                className="jobAdvertisingFilterSelect"
                value={filters.department}
                onChange={(e) => setFilters(prev => ({...prev, department: e.target.value}))}
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            
            <div className="jobAdvertisingFilterGroup">
              <label className="jobAdvertisingFilterLabel">Location</label>
              <select 
                className="jobAdvertisingFilterSelect"
                value={filters.location}
                onChange={(e) => setFilters(prev => ({...prev, location: e.target.value}))}
              >
                <option value="">All Locations</option>
                {locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>

            <div className="jobAdvertisingFilterGroup">
              <label className="jobAdvertisingFilterLabel">Job Type</label>
              <select 
                className="jobAdvertisingFilterSelect"
                value={filters.jobType}
                onChange={(e) => setFilters(prev => ({...prev, jobType: e.target.value}))}
              >
                <option value="">All Types</option>
                {jobTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="jobAdvertisingFilterGroup">
              <label className="jobAdvertisingFilterLabel">Experience</label>
              <select className="jobAdvertisingFilterSelect">
                <option value="">Any Experience</option>
                <option value="entry">Entry Level (0-2 years)</option>
                <option value="mid">Mid Level (3-5 years)</option>
                <option value="senior">Senior Level (5+ years)</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Jobs Section */}
      <section className="jobAdvertisingFeaturedSection">
        <div className="jobAdvertisingSectionHeader">
          <h2 className="jobAdvertisingSectionTitle">Featured Opportunities</h2>
          <p className="jobAdvertisingSectionSubtitle">
            Hand-picked positions from our partner companies
          </p>
        </div>
        <div className="jobAdvertisingFeaturedGrid">
          {jobListings.filter(job => job.featured).map(job => (
            <FeaturedJobCard key={job.id} job={job} />
          ))}
        </div>
      </section>

      {/* Main Job Listings */}
      <section className="jobAdvertisingListingsSection">
        <div className="jobAdvertisingSectionHeader">
          <h2 className="jobAdvertisingSectionTitle">All Job Opportunities</h2>
          <div className="jobAdvertisingTabContainer">
            <button 
              className={`jobAdvertisingTab ${activeTab === 'active' ? 'jobAdvertisingTabActive' : ''}`}
              onClick={() => setActiveTab('active')}
            >
              Active Positions
              <span className="jobAdvertisingTabCount">
                {jobListings.filter(job => job.status === 'active').length}
              </span>
            </button>
            <button 
              className={`jobAdvertisingTab ${activeTab === 'closed' ? 'jobAdvertisingTabActive' : ''}`}
              onClick={() => setActiveTab('closed')}
            >
              Closed Positions
              <span className="jobAdvertisingTabCount">
                {jobListings.filter(job => job.status === 'closed').length}
              </span>
            </button>
          </div>
        </div>

        <div className="jobAdvertisingListings">
          {filteredJobs.length > 0 ? (
            filteredJobs.map(job => (
              <JobCard key={job.id} job={job} />
            ))
          ) : (
            <div className="jobAdvertisingEmptyState">
              <div className="jobAdvertisingEmptyIllustration">📋</div>
              <h3>No matching jobs found</h3>
              <p>Try adjusting your search criteria or check back later for new positions.</p>
              <button 
                className="jobAdvertisingResetButton"
                onClick={() => {
                  setSearchTerm('');
                  setFilters({ department: '', location: '', jobType: '' });
                }}
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="jobAdvertisingBenefitsSection">
        <div className="jobAdvertisingSectionHeader">
          <h2 className="jobAdvertisingSectionTitle">Why Choose Our Platform?</h2>
          <p className="jobAdvertisingSectionSubtitle">
            We're committed to helping you find the right opportunity
          </p>
        </div>
        <div className="jobAdvertisingBenefitsGrid">
          <div className="jobAdvertisingBenefitCard">
            <div className="jobAdvertisingBenefitIcon">🎯</div>
            <h3>Curated Opportunities</h3>
            <p>Hand-picked positions from verified companies that match your skills and career goals.</p>
          </div>
          <div className="jobAdvertisingBenefitCard">
            <div className="jobAdvertisingBenefitIcon">⚡</div>
            <h3>Fast Application</h3>
            <p>One-click applications and quick response times from hiring managers.</p>
          </div>
          <div className="jobAdvertisingBenefitCard">
            <div className="jobAdvertisingBenefitIcon">🛡️</div>
            <h3>Verified Companies</h3>
            <p>All companies are thoroughly vetted to ensure legitimate opportunities.</p>
          </div>
          <div className="jobAdvertisingBenefitCard">
            <div className="jobAdvertisingBenefitIcon">📊</div>
            <h3>Career Insights</h3>
            <p>Get valuable insights about salary ranges and market trends.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="jobAdvertisingCtaSection">
        <div className="jobAdvertisingCtaContent">
          <h2 className="jobAdvertisingCtaTitle">Ready to Take the Next Step?</h2>
          <p className="jobAdvertisingCtaText">
            Join thousands of professionals who have found their dream jobs through our platform. 
            Create your profile and let opportunities find you.
          </p>
          <div className="jobAdvertisingCtaButtons">
            <button className="jobAdvertisingCtaButtonPrimary">
              Create Your Profile
            </button>
            <button className="jobAdvertisingCtaButtonSecondary">
              Subscribe to Job Alerts
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

// Featured Job Card Component
const FeaturedJobCard = ({ job }) => {
  return (
    <div className="jobAdvertisingFeaturedCard">
      {job.urgent && <div className="jobAdvertisingUrgentBadge">Urgent Hiring</div>}
      <div className="jobAdvertisingFeaturedCardContent">
        <div className="jobAdvertisingFeaturedCardHeader">
          <h3 className="jobAdvertisingFeaturedCardTitle">{job.title}</h3>
          <span className="jobAdvertisingFeaturedSalary">{job.salary}</span>
        </div>
        <p className="jobAdvertisingFeaturedCompany">{job.company}</p>
        <div className="jobAdvertisingFeaturedMeta">
          <span className="jobAdvertisingFeaturedMetaItem">📍 {job.location}</span>
          <span className="jobAdvertisingFeaturedMetaItem">⏱️ {job.type}</span>
          <span className="jobAdvertisingFeaturedMetaItem">💼 {job.department}</span>
        </div>
        <p className="jobAdvertisingFeaturedDescription">{job.description.substring(0, 120)}...</p>
        <div className="jobAdvertisingFeaturedCardActions">
          <button className="jobAdvertisingFeaturedApplyButton">
            Quick Apply
          </button>
          <button className="jobAdvertisingFeaturedSaveButton">
            💖 Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Job Card Component
const JobCard = ({ job }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`jobAdvertisingCard ${job.featured ? 'jobAdvertisingCardFeatured' : ''}`}>
      {job.urgent && <div className="jobAdvertisingUrgentBadge">Urgent Hiring</div>}
      
      <div className="jobAdvertisingCardHeader">
        <div className="jobAdvertisingCardInfo">
          <div className="jobAdvertisingCardTitleSection">
            <h3 className="jobAdvertisingCardTitle">{job.title}</h3>
            {job.featured && <span className="jobAdvertisingFeaturedTag">Featured</span>}
          </div>
          <p className="jobAdvertisingCardCompany">{job.company}</p>
          <div className="jobAdvertisingCardMeta">
            <span className="jobAdvertisingCardMetaItem">
              📍 {job.location}
            </span>
            <span className="jobAdvertisingCardMetaItem">
              ⏱️ {job.type}
            </span>
            <span className="jobAdvertisingCardMetaItem">
              💼 {job.department}
            </span>
            <span className="jobAdvertisingCardMetaItem">
              💰 {job.salary}
            </span>
            <span className="jobAdvertisingCardMetaItem">
              🎯 {job.experience}
            </span>
          </div>
        </div>
        <div className="jobAdvertisingCardActions">
          <button className="jobAdvertisingApplyButton">
            Apply Now
          </button>
          <button className="jobAdvertisingSaveButton">
            💖
          </button>
          <button 
            className="jobAdvertisingDetailsButton"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Less Details' : 'More Details'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="jobAdvertisingCardExpanded">
          <div className="jobAdvertisingExpandedContent">
            <div className="jobAdvertisingExpandedSection">
              <h4>Job Description</h4>
              <p>{job.description}</p>
            </div>
            
            <div className="jobAdvertisingExpandedSection">
              <h4>Key Responsibilities</h4>
              <ul className="jobAdvertisingRequirementsList">
                {job.responsibilities.map((resp, index) => (
                  <li key={index}>{resp}</li>
                ))}
              </ul>
            </div>

            <div className="jobAdvertisingExpandedSection">
              <h4>Requirements</h4>
              <ul className="jobAdvertisingRequirementsList">
                {job.requirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </div>

            <div className="jobAdvertisingExpandedSection">
              <h4>Benefits & Perks</h4>
              <div className="jobAdvertisingBenefitsList">
                {job.benefits.map((benefit, index) => (
                  <span key={index} className="jobAdvertisingBenefitTag">{benefit}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="jobAdvertisingCardFooter">
            <div className="jobAdvertisingFooterInfo">
              <span className="jobAdvertisingPostedDate">
                📅 Posted: {new Date(job.postedDate).toLocaleDateString()}
              </span>
              <span className="jobAdvertisingApplicants">
                👥 24 applicants
              </span>
            </div>
            <div className="jobAdvertisingFooterActions">
              <button className="jobAdvertisingSaveJobButton">
                💖 Save Job
              </button>
              <button className="jobAdvertisingShareButton">
                📤 Share
              </button>
              <button className="jobAdvertisingApplyButtonLarge">
                Apply for this Position
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobAdvertising;